import type { SourceListItem } from "#/types";

import { useAuth } from "#/feature/auth/auth-context";
import { useRequireAuth } from "#/feature/auth/use-require-auth";
import { BookStatus } from "#/types";
import { ArrowUpDown, Filter, Globe, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cx } from "styled-system/css";

import type { SortMode } from "./use-library";

import { BookCard } from "./book-card";
import {
  pageStyles,
  headerStyles,
  titleStyles,
  gridStyles,
  loadingStyles,
  emptyStyles,
  filterContainerStyles,
  filterInnerStyles,
  filterInputContainerStyles,
  filterInputStyles,
  filterSearchIconStyles,
  filterButtonStyles,
  dropdownStyles,
  dropdownItemStyles,
} from "./library.css";
import { useLibrary } from "./use-library";
import { useActivityScrollRestoration } from "./use-activity-scroll-restoration";

export function Library() {
  const allowed = useRequireAuth();
  const { user } = useAuth();
  useActivityScrollRestoration();
  const {
    nameFilter,
    setNameFilter,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    sortMode,
    setSortMode,
    crawlers,
    loadingState,
    filteredLibrary,
    fetchLibrary,
  } = useLibrary();

  if (!allowed) return null;

  return (
    <div className={pageStyles}>
      <LibraryHeader />

      <LibraryFilters
        nameFilter={nameFilter}
        setNameFilter={setNameFilter}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        sortMode={sortMode}
        setSortMode={setSortMode}
        crawlers={crawlers}
      />

      {loadingState ? (
        <div className={loadingStyles}>Loading library...</div>
      ) : (
        <div className={gridStyles} data-debug-id="library-grid">
          {filteredLibrary.map((book) => (
            <BookCard key={book.id} book={book} />
          ))}
          {filteredLibrary.length === 0 && (
            <div className={emptyStyles}>No books found matching your filters.</div>
          )}
        </div>
      )}
    </div>
  );
}

interface LibraryHeaderProps {
  // No props needed
}

const LibraryHeader = () => (
  <div className={headerStyles}>
    <h2 className={titleStyles}>My Collection</h2>
  </div>
);

interface LibraryFiltersProps {
  nameFilter: string;
  setNameFilter: (value: string) => void;
  sourceFilter: string;
  setSourceFilter: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  sortMode: SortMode;
  setSortMode: (value: SortMode) => void;
  crawlers: SourceListItem[];
}

const LibraryFilters = ({
  nameFilter,
  setNameFilter,
  sourceFilter,
  setSourceFilter,
  statusFilter,
  setStatusFilter,
  sortMode,
  setSortMode,
  crawlers,
}: LibraryFiltersProps) => {
  const [openDropdown, setOpenDropdown] = useState<"sort" | "source" | "status" | null>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideSort = !sortRef.current || !sortRef.current.contains(target);
      const isOutsideSource = !sourceRef.current || !sourceRef.current.contains(target);
      const isOutsideStatus = !statusRef.current || !statusRef.current.contains(target);

      if (isOutsideSort && isOutsideSource && isOutsideStatus) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openDropdown]);

  const getSortLabel = () => (sortMode === "recent" ? "Recently Read" : "Name (A-Z)");

  const getSourceLabel = () => {
    if (!sourceFilter) {
      return "All Sources";
    }
    const crawler = crawlers.find((c) => c.meta.source_id === sourceFilter);
    return crawler
      ? `${crawler.meta.source_id}${crawler.meta.needs_extension ? " (Ext)" : ""}`
      : sourceFilter;
  };

  const getStatusLabel = () => statusFilter || "All Statuses";

  return (
    <div className={filterContainerStyles}>
      <div className={filterInnerStyles}>
        <div className={filterInputContainerStyles}>
          <Search size={20} className={filterSearchIconStyles} />
          <input
            type="text"
            value={nameFilter}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setNameFilter(event.target.value)
            }
            placeholder="Enter novel title"
            className={filterInputStyles}
          />
        </div>
        <div style={{ position: "relative" }} ref={sortRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === "sort" ? null : "sort")}
            className={filterButtonStyles}
            aria-label="Sort"
          >
            <ArrowUpDown size={20} />
          </button>
          {openDropdown === "sort" && (
            <div className={dropdownStyles}>
              <button
                onClick={() => {
                  setSortMode("recent");
                  setOpenDropdown(null);
                }}
                className={dropdownItemStyles({ active: sortMode === "recent" })}
              >
                Recently Read
              </button>
              <button
                onClick={() => {
                  setSortMode("name");
                  setOpenDropdown(null);
                }}
                className={dropdownItemStyles({ active: sortMode === "name" })}
              >
                Name (A-Z)
              </button>
            </div>
          )}
        </div>
        <div style={{ position: "relative" }} ref={sourceRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === "source" ? null : "source")}
            className={filterButtonStyles}
            aria-label="Filter by source"
          >
            <Globe size={20} />
          </button>
          {openDropdown === "source" && (
            <div className={dropdownStyles}>
              <button
                onClick={() => {
                  setSourceFilter("");
                  setOpenDropdown(null);
                }}
                className={dropdownItemStyles({ active: !sourceFilter })}
              >
                All Sources
              </button>
              {crawlers.map((crawler) => (
                <button
                  key={crawler.meta.source_id}
                  onClick={() => {
                    setSourceFilter(crawler.meta.source_id);
                    setOpenDropdown(null);
                  }}
                  className={dropdownItemStyles({
                    active: sourceFilter === crawler.meta.source_id,
                  })}
                >
                  {crawler.meta.source_id}
                  {crawler.meta.needs_extension ? " (Ext)" : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ position: "relative" }} ref={statusRef}>
          <button
            onClick={() => setOpenDropdown(openDropdown === "status" ? null : "status")}
            className={filterButtonStyles}
            aria-label="Filter by status"
          >
            <Filter size={20} />
          </button>
          {openDropdown === "status" && (
            <div className={dropdownStyles}>
              <button
                onClick={() => {
                  setStatusFilter("");
                  setOpenDropdown(null);
                }}
                className={dropdownItemStyles({ active: !statusFilter })}
              >
                All Statuses
              </button>
              {Object.values(BookStatus).map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setStatusFilter(status);
                    setOpenDropdown(null);
                  }}
                  className={dropdownItemStyles({ active: statusFilter === status })}
                >
                  {status}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
